import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCandidates, getCriteriaTemplates, getWizardBlacklists } from '../campaign-wizard.api';
import { CriteriaTemplate, WizardBlacklistList } from '../campaign-wizard.types';
import { wz } from '../components/wizard-tokens';
import { WizLink, WizSelectPill } from '../components/wizard-ui';
import { supportsPostalRadius, supportsRadius } from './candidates/candidates-logic';
import { buildSettingRows, buildSummaryRows, SettingField, SummaryCandidates, withSearchFailure } from './review/review-model';
import type { WizardStepProps } from './step.types';

const SETTING_ICONS = {
  person: PersonOutlineIcon,
  schedule: ScheduleOutlinedIcon,
  mark_email_read: MarkEmailReadOutlinedIcon,
  how_to_reg: HowToRegOutlinedIcon,
  history: HistoryOutlinedIcon,
} as const;

const cardStyle: React.CSSProperties = {
  background: '#FFF',
  borderRadius: 12,
  boxShadow: wz.shadowCard,
};

/** Schritt 7 „Prüfen & starten“: Zusammenfassung aller Schritte und die Agent-Einstellungen. */
const StepReview: React.FC<WizardStepProps> = ({ wizard }) => {
  const { t } = useTranslation();
  const { state, patch, accounts, flowPlan, campaignId } = wizard;
  const [open, setOpen] = useState<SettingField | null>(null);
  const [lists, setLists] = useState<WizardBlacklistList[]>([]);
  const [templates, setTemplates] = useState<CriteriaTemplate[]>([]);
  const [candidates, setCandidates] = useState<SummaryCandidates | null>(null);

  useEffect(() => {
    let cancelled = false;
    void getWizardBlacklists()
      .then(res => !cancelled && setLists(res.lists))
      .catch(() => undefined);
    void getCriteriaTemplates()
      .then(res => !cancelled && setTemplates(res.templates))
      .catch(() => undefined);
    if (campaignId) {
      void getCandidates(campaignId)
        .then(res => !cancelled &&
            setCandidates({
              active: res.activeCount,
              total: res.total,
              searchStatus: res.searchStatus,
              analysisPending: res.analysisPending,
            })
          )
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const rows = useMemo(
    () =>
      buildSummaryRows(
        {
          state,
          estimate: wizard.estimate.value,
          lists,
          templates,
          candidates: withSearchFailure(candidates, wizard.searchFailure),
          flowPlan,
          radiusSupported: supportsRadius(wizard.account?.productType),
          postalSupported: supportsPostalRadius(wizard.account?.productType),
        },
        t
      ),
    [state, wizard.estimate.value, wizard.account?.productType, lists, templates, candidates, wizard.searchFailure, flowPlan, t]
  );
  const settings = useMemo(() => buildSettingRows(state, accounts, t), [state, accounts, t]);

  return (
    <>
      <div style={{ ...cardStyle, padding: '8px 28px' }}>
        {rows.map((row, i) => (
          <div
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '16px 0',
              borderBottom: i < rows.length - 1 ? `1px solid ${wz.border}` : 'none',
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 20, color: wz.primary }} />
            <div style={{ width: 130, fontSize: 15, fontWeight: 700 }}>{t(row.labelKey)}</div>
            <div
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 14,
                color: wz.text555,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              title={row.value}
            >
              {row.value}
            </div>
            <WizLink onClick={() => wizard.goTo(row.step)}>{t('wizRevEdit')}</WizLink>
          </div>
        ))}
      </div>

      <div
        style={{
          ...cardStyle,
          padding: '24px 28px',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{t('wizRevAgentTitle')}</div>
          <div style={{ fontSize: 14, color: wz.muted }}>{t('wizRevAgentSubtitle')}</div>
        </div>
        {settings.map(row => {
          const Icon = SETTING_ICONS[row.icon];
          // Offene Pflichtauswahl ist aufgeklappt und lässt sich nicht zuklappen.
          const isOpen = row.needsChoice === true || open === row.field;
          return (
            <div
              key={row.field}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
                padding: '12px 0',
                borderTop: `1px solid ${wz.border}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Icon sx={{ fontSize: 20, color: wz.muted }} />
                <div style={{ width: 150, fontSize: 14, color: wz.muted }}>{t(row.titleKey)}</div>
                <div
                  style={{
                    flex: 1,
                    fontSize: 15,
                    ...(row.needsChoice ? { color: wz.redFg, fontWeight: 700 } : null),
                  }}
                  data-testid={row.needsChoice ? `wiz-rev-choose-${row.field}` : undefined}
                >
                  {row.value}
                </div>
                {row.needsChoice ? null : (
                  <WizLink onClick={() => setOpen(isOpen ? null : row.field)}>
                    {isOpen ? t('wizRevDone') : t('wizRevChange')}
                  </WizLink>
                )}
              </div>
              {row.hint ? (
                <div style={{ paddingLeft: 186, fontSize: 13, color: wz.muted }}>{row.hint}</div>
              ) : null}
              {isOpen ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingLeft: 186 }}>
                  {row.options.map((option, optionIndex) => (
                    <WizSelectPill
                      key={`${optionIndex}-${option.label}`}
                      selected={option.selected}
                      onClick={() => patch(option.patch)}
                    >
                      {option.label}
                    </WizSelectPill>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default StepReview;
